import { ChartDataset, Tooltip, ScriptableContext } from "chart.js";
import { Chart as ChartJS, ChartData, ChartOptions, CategoryScale, LinearScale, PointElement, LineElement, Filler } from "chart.js";
import { Chart as ReactChart } from "react-chartjs-2";
import { TimePeriod } from "../../historicalChart.consants";
import { Inference, Side } from "@/app/data/api";
import { useMemo } from "react";
import { useInferencesLinesData } from "../../hooks/useInferenceLinesData";
import { PriceType } from "@/types/types";
import { externalTooltipHandler } from "./chart.tooltip";
import { createTradingTimeScale } from "@/utils/date.utils";
import { useDataContext } from "@/app/data/dataProvider";
import { DateTime } from "luxon";
import 'chartjs-adapter-luxon';



ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Filler,
  // CrosshairPlugin,
);

// Interaction.modes.interpolate = Interpolate;


const colors = {
  yColor: '#2E65A0',
  xColor: '#5D5F9D',
  offer: '#FB275A',
  bid: '#3FDA54',
  dealer: '#C9CADE',
}

// formats a trading-time axis tick (mapped back to real calendar ms) per period, roughly mirroring
// the previous timestack displayFormats
const formatHistoricalTick = (ms: number, timePeriod: TimePeriod): string => {
  const dt = DateTime.fromMillis(ms, { zone: 'America/New_York' });
  switch (timePeriod) {
    case TimePeriod['3H']:
    case TimePeriod['1D']:
      return dt.toFormat('t');        // 9:30 AM
    case TimePeriod['5D']:
      return dt.toFormat('ccc t');    // Mon 9:30 AM
    case TimePeriod['3Y']:
    case TimePeriod['5Y']:
    case TimePeriod['10Y']:
    case TimePeriod['20Y']:
    case TimePeriod['30Y']:
    case TimePeriod['40Y']:
    case TimePeriod['50Y']:
    case TimePeriod.Max:
      return dt.toFormat('LLL yyyy'); // Jul 2024
    default:                          // 1M, 3M, 6M, 1Y, Custom
      return dt.toFormat('LLL d');    // Jul 3
  }
}



export const Chart = ({
  timePeriod,
  sides,
  percentileActive,
  bidInferences,
  offerInferences,
  dealerInferences,
  priceType,
  times,
}: {
  timePeriod: TimePeriod;
  sides: Record<Side, boolean>;
  percentileActive: boolean;
  bidInferences: Inference[];
  offerInferences: Inference[];
  dealerInferences: Inference[];
  priceType: PriceType;
  times: string[];
}) => {
  const { finraHolidays } = useDataContext();

  // map calendar time -> cumulative trading time so the x-axis collapses nights/weekends/holidays
  const firstMs = times.length ? new Date(times[0]).getTime() : 0;
  const lastMs = times.length ? new Date(times[times.length - 1]).getTime() : 0;
  const scale = useMemo(() => createTradingTimeScale(firstMs, lastMs, finraHolidays), [firstMs, lastMs, finraHolidays]);

  // convert inference data to chart data
  const { bid, offer, dealer } = useInferencesLinesData({
    bidInferences,
    dealerInferences,
    offerInferences,
    percentileActive,
    priceType,
    toKey: scale.toKey,
  })


  const options = useMemo<ChartOptions<"line">>(() => {
    const options: ChartOptions<'line'> = {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      interaction: {
        mode: 'nearest',
        axis: 'x',
        intersect: false
      },
      scales: {
        y: {
          border: {
            display: false,
          },
          grid: {
            display: true,
            color: colors.yColor,
            drawTicks: true,
            drawOnChartArea: true,
          },
          ticks: {
            color: colors.yColor,
            padding: 10,
          }
        },
        x: {
          // linear trading-time axis: non-trading-hour time is compressed to a small fixed gap between sessions
          type: 'linear',
          min: scale.min,
          max: scale.max,
          border: {
            display: false,
          },
          grid: {
            display: true,
            color: colors.xColor,
            drawTicks: false,
            drawOnChartArea: true,
          },
          ticks: {
            color: colors.xColor,
            padding: 10,
            autoSkip: true,
            maxTicksLimit: 8,
            callback: (value: number | string) => formatHistoricalTick(scale.toMs(Number(value)), timePeriod),
          }
        }
      },
      layout: {
        padding: {
          left: 20,
          right: 20,
          top: 20,
          bottom: 20
        }
      },
      plugins: {
        filler: {
          propagate: true,
        },
        legend: {
          display: false,
        },
        tooltip: {
          caretPadding: 10,
          displayColors: false,
          position: 'nearest',
          enabled: false,
          external: externalTooltipHandler
        }
      }
    }
    return options;
  }, [timePeriod, scale])

  const data: ChartData<"line", any, unknown> = useMemo(() => {
    const showOnlyBid = sides[Side.bid] && !sides[Side.dealer] && !sides[Side.offer];
    const showOnlyDealer = sides[Side.dealer] && !sides[Side.bid] && !sides[Side.offer];
    const showOnlyOffer = sides[Side.offer] && !sides[Side.bid] && !sides[Side.dealer];


    const lineDataCommon: Partial<ChartDataset<"line", any>> = {
      borderWidth: 2,
      pointRadius: 0,
      pointHoverBackgroundColor: '#5D5F9D',
      pointHoverBorderWidth: 3,
      pointHoverRadius: 6,
    }

    const dashedDataCommon = {
      ...lineDataCommon,
      borderDash: [5, 5],
    }

    // lines
    const bidDataset: ChartDataset<"line", any> = {
      ...lineDataCommon,
      label: 'bid',
      data: bid.data,
      borderColor: colors.bid,
      hidden: !sides[Side.bid],
    }


    const dealerDataset: ChartDataset<"line", any> = {
      ...lineDataCommon,
      label: 'dealer',
      data: dealer.data,
      borderColor: colors.dealer,
      hidden: !sides[Side.dealer],
    }

    const offerDataset: ChartDataset<"line", any> = {
      ...lineDataCommon,
      label: 'offer',
      data: offer.data,
      borderColor: colors.offer,
      hidden: !sides[Side.offer],
    }

    // bid percentiles
    const bid5thPercentile: ChartDataset<"line", any> = {
      ...dashedDataCommon,
      label: 'bid 5th',
      data: bid.fifthPercentile,
      borderColor: colors.bid,

      hidden: !percentileActive || !sides[Side.bid],
      fill: 0,
      backgroundColor: getGradientFunc("rgba(34, 116, 45, 0.10)", "rgba(63, 218, 84, 0.20)"),
    }

    const bid95thPercentile: ChartDataset<"line", any> = {
      ...dashedDataCommon,
      label: 'bid 95th',
      data: bid.ninetyFifthPercentile,
      borderColor: colors.bid,

      hidden: !showOnlyBid,
      fill: 0,
      backgroundColor: getGradientFunc("rgba(63, 218, 84, 0.20)", "rgba(34, 116, 45, 0.10)"),
    }

    // offer percentiles
    const offer5thPercentile: ChartDataset<"line", any> = {
      ...dashedDataCommon,
      label: 'offer 5th',
      data: offer.fifthPercentile,
      borderColor: colors.offer,
      hidden: !percentileActive || !sides[Side.offer],
      fill: 2,
      backgroundColor: getGradientFunc("rgba(149, 23, 53, 0.60)", "rgba(245, 38, 88, 0.10)"),
    }

    const offer95thPercentile: ChartDataset<"line", any> = {
      ...dashedDataCommon,
      label: 'offer 95th',
      data: offer.ninetyFifthPercentile,
      borderColor: colors.offer,
      hidden: !showOnlyOffer,
      fill: 2,
      backgroundColor: getGradientFunc("rgba(245, 38, 88, 0.10)", "rgba(149, 23, 53, 0.60)",),
    }

    // dealer percentiles
    const dealer5thPercentile: ChartDataset<"line", any> = {
      ...dashedDataCommon,
      label: 'dealer 5th',
      data: dealer.fifthPercentile,
      borderColor: colors.dealer,
      hidden: !showOnlyDealer,
      fill: 1,
      backgroundColor: getGradientFunc("rgba(201, 202, 222, 0.10)", "rgba(201, 202, 222, 0.60)"),
    }

    const dealer95thPercentile: ChartDataset<"line", any> = {
      ...dashedDataCommon,
      label: 'dealer 95th',
      data: dealer.ninetyFifthPercentile,
      borderColor: colors.dealer,
      hidden: !showOnlyDealer,
      fill: 1,
      backgroundColor: getGradientFunc("rgba(201, 202, 222, 0.60)", "rgba(201, 202, 222, 0.10)",),
    }


    const data: ChartData<"line", any, unknown> = {
      datasets: [
        bidDataset,
        dealerDataset,
        offerDataset,
        bid5thPercentile,
        bid95thPercentile,
        dealer5thPercentile,
        dealer95thPercentile,
        offer5thPercentile,
        offer95thPercentile,
      ]
    }

    return data;
  }, [sides, percentileActive, bid, offer, dealer])

  return (
    <ReactChart
      key="historical-chart"
      type="line"
      options={options}
      data={data}
    />
  )
}


const getGradientFunc = (start: string, stop: string) => (context: ScriptableContext<"line">) => {
  const chart = context.chart;
  const { ctx, chartArea } = chart;

  if (!chartArea) {
    // This case happens on initial chart load
    return;
  }
  const gradientFill = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
  gradientFill.addColorStop(0, start);
  gradientFill.addColorStop(1, stop);

  return gradientFill
}